import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, MessageCircle, ChevronDown, ChevronRight, ArrowRight, GraduationCap, Facebook, Instagram, Linkedin, Twitter, Youtube, Send, Building2, FileText, ShieldCheck, Award, BadgeCheck, BarChart3, CalendarCheck, ClipboardCheck, Handshake, Infinity, Laptop, Layers, MonitorSmartphone, PenTool, Presentation, Route, Settings, TrendingUp, Users, Trophy, CheckCircle2, Sparkles, Cloud, Code2, Megaphone } from "lucide-react";

const ICONS_MAP = { ShieldCheck, Award, BadgeCheck, BarChart3, CalendarCheck, ClipboardCheck, Handshake, Infinity, Laptop, Layers, MonitorSmartphone, PenTool, Presentation, Route, Settings, TrendingUp, Users, Trophy, CheckCircle2, Sparkles, GraduationCap };
import BrandLogo from "@/components/BrandLogo";
import api from "@/lib/api";

const SOCIALS = [["facebook", Facebook], ["instagram", Instagram], ["linkedin", Linkedin], ["twitter", Twitter], ["youtube", Youtube], ["telegram", Send]];
const LEGAL_PATHS = ["/privacy-policy", "/terms-conditions", "/disclaimer"];
const DEFAULT_SERVICE_LINKS = [
  { label: "Book Demo", link: "/contact-us" },
  { label: "Request Certificate", link: "/contact-us" },
  { label: "Verify Certificate", link: "/contact-us" },
  { label: "View Latest Jobs", link: "https://www.jobbasket.in" },
  { label: "Support", link: "/contact-us" },
];
const DEFAULT_OPPORTUNITY_LINKS = [
  { label: "Careers", link: "https://www.jobbasket.in" },
  { label: "Become an Instructor", link: "https://www.jobbasket.in" },
  { label: "Create Account", link: "/signup" },
];
const HUB_ICONS = { "Data Science, Analytics & AI": BarChart3, "Cloud Computing & DevOps": Cloud, "Networking & Cybersecurity": ShieldCheck, "Software & Web Development": Code2, "Digital Marketing": Megaphone };
const HUB_TECH = [
  "https://cdn.simpleicons.org/python",
  "https://cdn.jsdelivr.net/npm/simple-icons@10/icons/amazonaws.svg",
  "https://cdn.jsdelivr.net/npm/simple-icons@10/icons/microsoftazure.svg",
  "https://cdn.simpleicons.org/googlecloud",
  "https://cdn.simpleicons.org/react",
  "https://cdn.simpleicons.org/javascript",
  "https://cdn.jsdelivr.net/npm/simple-icons@10/icons/powerbi.svg",
  "https://cdn.jsdelivr.net/npm/simple-icons@10/icons/microsoft.svg",
  "https://cdn.simpleicons.org/docker",
  "https://cdn.simpleicons.org/linux",
];

function SmartLink({ to, className, testid, children }) {
  if (to?.startsWith("http") || to?.endsWith(".xml")) {
    return <a href={to} className={className} data-testid={testid} {...(to.startsWith("http") ? { target: "_blank", rel: "noopener noreferrer" } : {})}>{children}</a>;
  }
  return <Link to={to || "#"} className={className} data-testid={testid}>{children}</Link>;
}

function Column({ id, title, icon: Icon, open, setOpen, testid, className = "", children }) {
  const isOpen = open === id;
  return (
    <div className={`border-white/10 ${className}`} data-testid={testid}>
      <button type="button" onClick={() => setOpen(isOpen ? null : id)} aria-expanded={isOpen}
        className="flex lg:hidden w-full items-center justify-between py-4 text-left" data-testid={`${testid}-toggle`}>
        <span className="text-sm font-extrabold text-white flex items-center gap-2.5"><Icon className="w-4.5 h-4.5 w-5 h-5 text-[#FFC72C]" /> {title}</span>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>
      <p className="hidden lg:flex text-sm font-extrabold text-white items-center gap-2.5 mb-5"><Icon className="w-5 h-5 text-[#FFC72C]" /> {title}</p>
      <div className={`${isOpen ? "block" : "hidden"} lg:block pb-5 lg:pb-0`}>{children}</div>
    </div>
  );
}

function LinkList({ links, testid }) {
  return (
    <div className="flex flex-col gap-2.5 text-sm">
      {links.map((l, i) => (
        <SmartLink key={i} to={l.link} testid={`${testid}-${i}`}
          className="group flex items-center gap-1.5 text-slate-400 hover:text-[#FFC72C] transition-colors">
          <ChevronRight className="w-3.5 h-3.5 text-[#FFC72C] shrink-0" /> {l.label}
        </SmartLink>
      ))}
    </div>
  );
}

export default function Footer() {
  const [s, setS] = useState(null);
  const [branding, setBranding] = useState(null);
  const [avatars, setAvatars] = useState([]);
  const [open, setOpen] = useState(null);
  const [hub, setHub] = useState(null);

  useEffect(() => {
    api.get("/website/sections/footer").then((r) => setS(r.data)).catch(() => setS({}));
    api.get("/website/sections/branding").then((r) => setBranding(r.data)).catch(() => {});
    api.get("/website/sections/home_content").then((r) => setAvatars((r.data.testimonials || []).filter((t) => t.photo).slice(0, 3).map((t) => t.photo))).catch(() => {});
    api.get("/public/seo-hub").then((r) => setHub(r.data)).catch(() => setHub({ enabled: false }));
  }, []);

  if (!s) return <footer className="bg-[#0F1115] mt-28 min-h-[957px] lg:min-h-[617px]" data-testid="footer" aria-hidden="true" />;
  const d = (k, v) => (s[k] != null && s[k] !== "" ? s[k] : v);
  const companyLinks = s.company_links?.length ? s.company_links : [];
  const serviceLinks = s.service_links?.length ? s.service_links : DEFAULT_SERVICE_LINKS;
  const opportunityLinks = s.opportunity_links?.length ? s.opportunity_links : DEFAULT_OPPORTUNITY_LINKS;
  const resourceLinks = s.resource_links?.length ? s.resource_links : [];
  const legalLinks = resourceLinks.filter((l) => LEGAL_PATHS.includes(l.link));
  const badges = s.badges?.length ? s.badges : [];
  const socials = branding?.socials || {};
  const waNumber = (s.whatsapp || "").replace(/\D/g, "");

  return (
    <footer className="bg-[#0F1115] text-slate-300 mt-28" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {d("cta_title", "") && (
          <div className="relative -top-16 rounded-3xl bg-white shadow-2xl border border-slate-100 overflow-hidden" data-testid="footer-cta-card">
            <div className="absolute -right-10 -top-12 w-40 h-40 rounded-full bg-[#FFC72C]" aria-hidden="true" />
            <div className="absolute -left-8 -bottom-14 w-32 h-32 rounded-full bg-[#FFE08A]" aria-hidden="true" />
            <div className="relative flex flex-col lg:flex-row items-center gap-6 lg:gap-8 p-6 sm:p-8 lg:p-10">
              <span className="w-14 h-14 rounded-2xl bg-[#FFC72C] flex items-center justify-center shrink-0">
                <GraduationCap className="w-7 h-7 text-[#17263B]" />
              </span>
              <div className="flex-1 text-center lg:text-left">
                <h3 className="text-xl sm:text-2xl font-extrabold text-[#17263B] tracking-tight" data-testid="footer-cta-title">{s.cta_title}</h3>
                {s.cta_subtitle && <p className="text-sm text-slate-500 mt-1" data-testid="footer-cta-subtitle">{s.cta_subtitle}</p>}
              </div>
              {s.cta_link && s.cta_label && (
                <SmartLink to={s.cta_link} testid="footer-cta-button"
                  className="inline-flex items-center gap-2 rounded-xl bg-[#FFC72C] hover:bg-[#F5B800] text-[#17263B] font-bold px-7 py-3.5 text-sm transition-all hover:-translate-y-0.5 shrink-0">
                  {s.cta_label} <ArrowRight className="w-4 h-4" />
                </SmartLink>
              )}
              {avatars.length > 0 && (
                <div className="flex items-center gap-3 shrink-0 lg:pl-8 lg:border-l lg:border-slate-200" data-testid="footer-cta-learners">
                  <div className="flex -space-x-2.5">
                    {avatars.map((a, i) => <img key={i} src={a} alt="" className="w-10 h-10 rounded-full object-cover border-2 border-white shadow" loading="lazy" />)}
                  </div>
                  <div>
                    <p className="font-extrabold text-[#17263B] text-sm leading-tight">{d("cta_count", "")}</p>
                    <p className="text-xs text-slate-500">{d("cta_count_label", "")}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="lg:grid lg:grid-cols-[1.2fr_2fr_1.2fr] lg:gap-10 -mt-6 pb-12">
          <div className="pb-8 lg:pb-0" data-testid="footer-brand">
            {s.footer_logo ? (
              <img src={s.footer_logo} alt={branding?.site_name || "Training Basket"} className="h-11 w-auto" data-testid="footer-logo" />
            ) : (
              <BrandLogo className="h-11 w-auto" light />
            )}
            {branding?.tagline && <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-slate-500 mt-2">{branding.tagline}</p>}
            <p className="text-sm text-slate-400 leading-relaxed mt-4 max-w-xs" data-testid="footer-description">
              {d("description", "")}
            </p>
            <div className="flex flex-wrap gap-2.5 mt-6" data-testid="footer-socials">
              {SOCIALS.map(([key, Icon]) => socials[key] ? (
                <a key={key} href={socials[key]} target="_blank" rel="noopener noreferrer" aria-label={key}
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-slate-300 hover:bg-[#FFC72C] hover:text-[#17263B] transition-colors"
                  data-testid={`footer-social-${key}`}>
                  <Icon className="w-4.5 h-4.5 w-5 h-5" />
                </a>
              ) : null)}
            </div>
          </div>

          <div className="grid lg:grid-cols-2" data-testid="footer-nav-grid">
            <Column id="company" title={d("col_company_title", "Training Basket")} icon={Building2} open={open} setOpen={setOpen} testid="footer-col-company" className="border-b lg:border-r lg:pr-8 lg:pb-8">
              <LinkList links={companyLinks} testid="footer-company-link" />
            </Column>
            <Column id="services" title={d("col_services_title", "Student Services")} icon={GraduationCap} open={open} setOpen={setOpen} testid="footer-col-services" className="border-b lg:pl-8 lg:pb-8">
              <LinkList links={serviceLinks} testid="footer-service-link" />
            </Column>
            <Column id="opportunities" title={d("col_opportunities_title", "Opportunities")} icon={Users} open={open} setOpen={setOpen} testid="footer-col-opportunities" className="border-b lg:border-r lg:border-b-0 lg:pr-8 lg:pt-8">
              <LinkList links={opportunityLinks} testid="footer-opportunity-link" />
            </Column>
            <Column id="resources" title={d("col_resources_title", "Resources")} icon={FileText} open={open} setOpen={setOpen} testid="footer-col-resources" className="border-b lg:border-b-0 lg:pl-8 lg:pt-8">
              <LinkList links={resourceLinks} testid="footer-resource-link" />
            </Column>
          </div>

          <Column id="contact" title={d("col_contact_title", "Contact Us")} icon={Phone} open={open} setOpen={setOpen} testid="footer-col-contact" className="border-b lg:border-0">
            <div className="flex flex-col gap-3 text-sm">
              {s.phone_1 && <a href={`tel:${s.phone_1.replace(/\s/g, "")}`} className="flex items-center gap-2.5 text-slate-400 hover:text-[#FFC72C] transition-colors" data-testid="footer-phone-1"><Phone className="w-4 h-4 text-[#FFC72C] shrink-0" /> {s.phone_1}</a>}
              {s.phone_2 && <a href={`tel:${s.phone_2.replace(/\s/g, "")}`} className="flex items-center gap-2.5 text-slate-400 hover:text-[#FFC72C] transition-colors" data-testid="footer-phone-2"><Phone className="w-4 h-4 text-[#FFC72C] shrink-0" /> {s.phone_2}</a>}
              {waNumber && <a href={`https://wa.me/${waNumber}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 text-slate-400 hover:text-[#FFC72C] transition-colors" data-testid="footer-whatsapp"><MessageCircle className="w-4 h-4 text-[#FFC72C] shrink-0" /> {s.whatsapp} (WhatsApp)</a>}
              {s.email && <a href={`mailto:${s.email}`} className="flex items-center gap-2.5 text-slate-400 hover:text-[#FFC72C] transition-colors" data-testid="footer-email"><Mail className="w-4 h-4 text-[#FFC72C] shrink-0" /> {s.email}</a>}
              {s.address && <p className="flex items-start gap-2.5 text-slate-400" data-testid="footer-address"><MapPin className="w-4 h-4 text-[#FFC72C] shrink-0 mt-0.5" /> {s.address}</p>}
              {s.division_note && (
                <p className="text-xs text-slate-500 pt-3 border-t border-white/10" data-testid="footer-division">
                  Training Basket is a division of <span className="text-[#FFC72C] font-semibold">{s.division_note}</span>
                </p>
              )}
              {s.promo_active === true && d("promo_title", "") && (
                <SmartLink to={d("promo_link", "/courses")} testid="footer-promo"
                  className="group flex items-center gap-3.5 rounded-2xl border border-white/15 hover:border-[#FFC72C]/60 bg-white/5 hover:bg-white/10 p-4 mt-1 transition-colors">
                  <span className="w-10 h-10 rounded-xl bg-[#FFC72C] flex items-center justify-center shrink-0">
                    <GraduationCap className="w-5 h-5 text-[#17263B]" />
                  </span>
                  <div className="flex-1 min-w-0">
                    {s.promo_label && <p className="font-serif italic text-[#FFC72C] text-xs leading-tight">{s.promo_label}</p>}
                    <p className="font-extrabold text-white text-sm leading-tight truncate">{s.promo_title}</p>
                  </div>
                  <span className="w-8 h-8 rounded-full border border-[#FFC72C]/60 text-[#FFC72C] flex items-center justify-center group-hover:bg-[#FFC72C] group-hover:text-[#17263B] transition-colors shrink-0">
                    <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </SmartLink>
              )}
            </div>
          </Column>
        </div>
      </div>

      {hub?.enabled && (hub.categories || []).length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-14" data-testid="seo-hub">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <div className="max-w-xl">
              <p className="text-xs font-extrabold uppercase tracking-[0.25em] text-[#FFC72C]" data-testid="seo-hub-eyebrow">{hub.eyebrow}</p>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight mt-2" data-testid="seo-hub-heading">{hub.heading}</h2>
              {hub.subheading && <p className="text-sm text-slate-400 mt-2" data-testid="seo-hub-subheading">{hub.subheading}</p>}
            </div>
            <div className="hidden md:flex flex-wrap gap-2.5 shrink-0" data-testid="seo-hub-tech" aria-hidden="true">
              {((hub.tech_logos || []).length ? hub.tech_logos : HUB_TECH.map((u) => ({ url: u, alt: "" }))).map((t, i) => (
                <span key={i} className="w-11 h-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                  <img src={t.url} alt={t.alt || ""} loading="lazy" className="w-6 h-6" />
                </span>
              ))}
            </div>
          </div>
          <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.02] divide-y divide-white/10">
            {hub.categories.map((cat) => {
              const Icon = HUB_ICONS[cat.name] || Layers;
              return (
                <div key={cat.name} className="grid lg:grid-cols-[260px_1fr] gap-4 lg:gap-8 px-5 sm:px-7 py-6" data-testid={`seo-hub-cat-${cat.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                  <div className="flex items-center gap-3.5">
                    <span className="w-11 h-11 rounded-full bg-[#FFC72C] flex items-center justify-center shrink-0">
                      <Icon className="w-5 h-5 text-[#17263B]" />
                    </span>
                    <span className="text-white font-extrabold text-[15px] leading-snug">{cat.name}</span>
                  </div>
                  <nav className="flex flex-wrap items-center gap-y-2.5 text-sm" aria-label={`${cat.name} courses`}>
                    {cat.pages.map((p, i) => (
                      <span key={p.url} className="flex items-center">
                        <a href={p.url} className="text-slate-300 hover:text-[#FFC72C] font-medium transition-colors" data-testid={`seo-hub-link-${p.url.slice(1).replace(/\//g, "-")}`}>{p.label}</a>
                        {i < cat.pages.length - 1 && <span className="mx-3 text-[#FFC72C]/70 select-none" aria-hidden="true">|</span>}
                      </span>
                    ))}
                  </nav>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-slate-500">
          <p data-testid="footer-copyright">© {new Date().getFullYear()} {d("copyright", "Training Basket. All rights reserved.")}</p>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2" data-testid="footer-legal-links">
            {(legalLinks.length ? legalLinks : [
              { label: "Privacy Policy", link: "/privacy-policy" },
              { label: "Terms & Conditions", link: "/terms-conditions" },
              { label: "Disclaimer", link: "/disclaimer" },
            ]).map((l) => (
              <SmartLink key={l.link} to={l.link} testid={`footer-legal-${l.link.slice(1)}`} className="hover:text-[#FFC72C] transition-colors">{l.label}</SmartLink>
            ))}
          </div>
          {badges.length > 0 && (
            <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2" data-testid="footer-badges">
              {badges.map((b, i) => {
                const Icon = ICONS_MAP[b.icon] || ShieldCheck;
                return <span key={i} className="flex items-center gap-1.5 text-slate-400" data-testid={`footer-badge-${i}`}><Icon className="w-4 h-4 text-[#FFC72C]" /> {b.label}</span>;
              })}
            </div>
          )}
        </div>
      </div>
    </footer>
  );
}
